import {
  createContext,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
} from 'react';
import { useLocation, useParams } from 'react-router';
import { DetailFilterContextType } from '../types/context';
import {
  GlobalLocationStateType,
  MatchMode,
  SelectedFilter,
} from '../types/types';
import { useDatasetContext } from '../hooks/context/useDatasetContext';
import {
  getColumnByFilterParam,
  getDefaultFilterValue,
} from '../utils/managerUtils';
import { getSupportedDefaultMatchMode } from '../utils/filters/getSupportedDefaultMatchMode';
import useRoutes from '../hooks/useRoutes';
import { processFetchedSlaveFilter } from '../utils/processFetchedSlaveFilter';
import { processFetchedSlaveDataset } from '../utils/processFetchedSlaveDataset';
import { useDataset } from '../api/hooks/useDataset';
import { useFilter } from '../api/hooks/useFilter';
import { useSelectedFiltersContext } from '../hooks/context/useSelectedFiltersContext';
import { useSelectedRowsContext } from '../hooks/context/useSelectedRowsContext';
import { isEqual } from 'lodash';
import { getSelectedFilterLabel } from '../utils/filters/getSelectedFilterLabel';

const defaultValues: DetailFilterContextType = {
  isProvided: false,
  clearSelectedFilters: () => {
    throw new Error('clearSelectedFilters must be implemented');
  },
  setDefaultMasterPopoverFilters: () => {
    throw new Error('setDefaultMasterPopoverFilters must be implemented');
  },
  submitMasterPopoverFilters: () => {
    throw new Error('submitMasterPopoverFilters must be implemented');
  },
  setDefaultSlavePopoverFilters: () => {
    throw new Error('setDefaultSlavePopoverFilters must be implemented');
  },
  submitSlavePopoverFilters: () => {
    throw new Error('submitSlavePopoverFilters must be implemented');
  },
};

export const DetailFilterContext =
  createContext<DetailFilterContextType>(defaultValues);

const DetailFilterContextProvider = ({ children }: { children: ReactNode }) => {
  const {
    filter,
    slaveFilters,
    dataset,
    slaveDatasets,
    slaveLazyStates,
    activeFilters,
    setSlaveDatasets,
    setSlaveLazyStates,
    setSlaveFilters,
  } = useDatasetContext();
  const { resetSlaveSelectedRows } = useSelectedRowsContext();
  const {
    selectedFilters,
    popoverFilters,
    setSelectedFilters,
    setPopoverFilters,
  } = useSelectedFiltersContext();

  const { datasetId, subsetId, itemId } = useParams<{
    datasetId: string;
    subsetId?: string;
    itemId?: string;
  }>();
  const { pathname } = useLocation<GlobalLocationStateType>();
  const routes = useRoutes();

  const { fetch: fetchDataset } = useDataset(subsetId ?? '');

  const { fetch: fetchFilter, isLoading: isFilterLoading } = useFilter();

  const isMasterFilter =
    pathname === routes.datasetDetailMasterFilter(datasetId || '');
  const isSlaveFilter =
    pathname ===
    routes.subsetDetailSlaveFilter(
      datasetId || '',
      itemId || '',
      subsetId || ''
    );

  // SLAVE FILTERS
  const getSlaveLazyStateFilterByFilterParam = (filterParam) => {
    let filter: null | any = null;
    if (!subsetId || !slaveLazyStates[subsetId] || !slaveDatasets[subsetId]) {
      return filter;
    }

    const slaveLazyState = slaveLazyStates[subsetId] as any;
    const columnWithThisFilter = getColumnByFilterParam(
      filterParam,
      slaveDatasets[subsetId]
    );

    if (columnWithThisFilter && slaveLazyState.filters) {
      const lazyStateFilter =
        slaveLazyState.filters[columnWithThisFilter.name]?.constraints[0];
      if (lazyStateFilter) {
        filter = lazyStateFilter;
      }
    }
    return filter;
  };

  const setDefaultSlavePopoverFilters = useCallback(
    (
      getFromLazyState: boolean = false,
      actualFilter: any = undefined,
      actualDataset: any = undefined
    ) => {
      const contextFilter =
        actualFilter ?? (slaveFilters[subsetId ?? ''] as any);
      const contextDataset =
        actualDataset ?? (slaveDatasets[subsetId ?? ''] as any);

      const filterItems = contextFilter?.items ?? [];
      const newPopoverFilters = [
        {
          id: contextDataset?.datasetId,
          caption: contextFilter?.caption,
          items: [
            ...filterItems.map((item) => {
              let isSelected = false;
              if (getFromLazyState) {
                const lazyStateFilter = getSlaveLazyStateFilterByFilterParam(
                  item.name
                );
                if (lazyStateFilter && lazyStateFilter.value !== null) {
                  isSelected = true;
                }
              }
              return {
                ...item,
                value: isSelected,
              };
            }),
          ],
        },
      ];

      if (getFromLazyState) {
        submitSlavePopoverFilters(newPopoverFilters, true, true);
      }
      setPopoverFilters(newPopoverFilters);
    },
    [popoverFilters, slaveFilters, subsetId, slaveLazyStates, slaveDatasets]
  );

  const submitSlavePopoverFilters = useCallback(
    (filters, getValueFromLazyState = false, initialSubmit = false) => {
      const newSelectedFilters: any[] = [];

      filters.forEach(({ items, id, caption }) => {
        items.forEach((item) => {
          if (item.value) {
            let matchMode = getSupportedDefaultMatchMode(item);
            let value: any = '';
            const foundSelectedFilter = selectedFilters.find(
              ({ name, datasetId }) => name === item.name && datasetId === id
            );
            if (foundSelectedFilter) {
              value = foundSelectedFilter.value;
              matchMode = foundSelectedFilter.matchMode;
            } else if (['float', 'integer'].includes(item.type)) {
              value = '0';
            } else if (item.type === 'boolean') {
              value = false;
            }

            if (id === subsetId && getValueFromLazyState) {
              const lazyStateFilter = getSlaveLazyStateFilterByFilterParam(
                item.name
              );
              if (lazyStateFilter && lazyStateFilter.value !== null) {
                matchMode = lazyStateFilter.matchMode;
                value = lazyStateFilter.value;
              }
            }
            const newFilter: any = {
              name: item.name,
              datasetId: id,
              label: getSelectedFilterLabel({
                filterDatasetId: id,
                masterDatasetId: dataset?.datasetId,
                caption,
                itemLabel: item.label,
              }),
              hint: item.hint,
              type: item.type,
              control: { ...item.control },
              matchMode,
              value,
              error: null,
            };
            newSelectedFilters.push(newFilter);
          }
        });
      });

      const changedFilters = !isEqual(filters, popoverFilters);

      if (changedFilters && !initialSubmit) {
        resetSlaveSelectedRows();
      }

      setSelectedFilters(newSelectedFilters);
    },
    [popoverFilters, subsetId, slaveLazyStates, slaveDatasets, isSlaveFilter]
  );

  const handleExternalSlaveFilterUrl = useCallback(
    async (subsetId: string) => {
      const dataset = await fetchDataset();
      processFetchedSlaveDataset(
        dataset,
        subsetId,
        setSlaveDatasets,
        setSlaveLazyStates
      );

      if (dataset?.filterId) {
        const filter = await fetchFilter(dataset.filterId, dataset.datasetId);

        if (filter) {
          processFetchedSlaveFilter(
            filter,
            subsetId,
            dataset,
            setSlaveLazyStates,
            (newFilter) =>
              setSlaveFilters((v) => ({
                ...v,
                [subsetId]: newFilter,
              }))
          );
          setDefaultSlavePopoverFilters(true, filter.filter, dataset);
        }
      }

      return;
    },
    [
      fetchDataset,
      fetchFilter,
      setSlaveDatasets,
      setSlaveLazyStates,
      setSlaveFilters,
      setDefaultSlavePopoverFilters,
    ]
  );

  //MASTER FILTERS
  const submitMasterPopoverFilters = useCallback(
    (filters, getValueFromActiveFilters = false) => {
      const newSelectedFilters: any[] = [];
      filters.forEach(({ items, id, caption }) => {
        items.forEach((item) => {
          if (item.value) {
            let matchMode = getSupportedDefaultMatchMode(item);
            let value: any = '';
            const foundSelectedFilter = selectedFilters.find(
              ({ name, datasetId }) => name === item.name && datasetId === id
            );
            if (foundSelectedFilter) {
              value = foundSelectedFilter.value;
              matchMode = foundSelectedFilter.matchMode;
            } else {
              value = getDefaultFilterValue(item);
            }
            if (getValueFromActiveFilters) {
              let activeFilter;
              if (id === dataset?.datasetId) {
                activeFilter = activeFilters?.[item.name];
              } else {
                activeFilter = activeFilters?.[`${id}>`]?.[item.name];
              }
              if (activeFilter) {
                matchMode = Object.keys(activeFilter)[0] as MatchMode;
                value = activeFilter[matchMode];
              }
            }

            const newFilter: SelectedFilter = {
              name: item.name,
              datasetId: id,
              label: getSelectedFilterLabel({
                filterDatasetId: id,
                masterDatasetId: dataset?.datasetId,
                caption,
                itemLabel: item.label,
              }),
              hint: item.hint,
              type: item.type,
              control: { ...item.control },
              matchMode,
              value,
              error: null,
            };
            newSelectedFilters.push(newFilter);
          }
        });
      });
      setSelectedFilters(newSelectedFilters);
    },
    [selectedFilters, activeFilters, dataset]
  );

  const setDefaultMasterPopoverFilters = useCallback(
    (getFromActiveFilters = false) => {
      const filterItems = filter?.items ?? [];
      const filterSubsets = filter?.subsets ?? [];

      const newPopoverFilters = [
        {
          id: dataset?.datasetId,
          caption: filter?.caption,
          items: [
            ...filterItems.map((item) => {
              let isSelected = false;
              if (getFromActiveFilters) {
                if (activeFilters?.[item.name]) {
                  isSelected = true;
                }
              }
              return {
                ...item,
                value: isSelected,
              };
            }),
          ],
        },
      ];
      filterSubsets.forEach((subset) => {
        newPopoverFilters.push({
          id: subset.subsetId,
          caption: subset.caption,
          items: subset.items.map((item) => {
            let value = false;
            const activeSubset = activeFilters?.[`${subset.subsetId}>`];
            if (activeSubset && activeSubset[item.name]) {
              value = true;
            }
            return { ...item, value: value };
          }),
        });
      });
      if (getFromActiveFilters) {
        submitMasterPopoverFilters(newPopoverFilters, true);
      }
      setPopoverFilters(newPopoverFilters);
    },
    [filter, activeFilters, dataset, submitMasterPopoverFilters]
  );
  // Fetch filters only when dependencies available
  useEffect(() => {
    if (isMasterFilter && filter) {
      setDefaultMasterPopoverFilters(true);
      return;
    }
  }, [filter]);

  // If the subset and subset filter is not loaded, load it from the url.
  useEffect(() => {
    if (
      subsetId &&
      itemId &&
      !isFilterLoading &&
      slaveDatasets[subsetId ?? ''] === undefined &&
      slaveFilters[subsetId ?? ''] === undefined
    ) {
      handleExternalSlaveFilterUrl(subsetId);
      return;
    }

    if (isSlaveFilter) {
      setDefaultSlavePopoverFilters(true);
    }
  }, [isMasterFilter, subsetId]);

  const value = useMemo(
    () => ({
      isProvided: true,
      clearSelectedFilters: () => setSelectedFilters([]),
      setDefaultMasterPopoverFilters,
      submitMasterPopoverFilters,
      setDefaultSlavePopoverFilters,
      submitSlavePopoverFilters,
    }),
    [
      setDefaultMasterPopoverFilters,
      submitMasterPopoverFilters,
      setDefaultSlavePopoverFilters,
      submitSlavePopoverFilters,
    ]
  );

  return (
    <DetailFilterContext.Provider value={value}>
      {children}
    </DetailFilterContext.Provider>
  );
};

export default DetailFilterContextProvider;
